import { PropertyConstants } from '../constants/PropertyConstants';

import ApplicationsSummary from '../models/ApplicationsSummary';
import ContactEmailDeliverability from '../models/ContactEmailDeliverability';
import ExternalTour from '../models/ExternalTour';
import SingleFamilyContract from '../models/SingleFamilyContract';
import Unit from '../models/Unit';
import VrModel from '../models/VrModel';

const postingUtils = {
  formatPriceFromApi(lowPrice: string | undefined, highPrice: string | undefined) {
    if (typeof lowPrice === 'undefined') {
      return '';
    }
    if (lowPrice !== highPrice) {
      return `${lowPrice}-${highPrice}`;
    }
    return lowPrice;
  },
  formatBedsFromApi(beds: number | undefined) {
    return (beds || beds === 0) && beds >= 0 ? beds : '';
  },
  transformPropertyListingTypeForApi(formData: {
    listingType: keyof typeof PropertyConstants.LISTING_CODES_BY_TYPE;
    propertyType: (typeof PropertyConstants.PROPERTY_TYPES)[keyof typeof PropertyConstants.PROPERTY_TYPES];
    fullAddress: string;
    unitNumber?: string;
    numUnits?: string;
  }) {
    const { LISTING_ENUM_BY_TYPE, PROPERTY_ENUM_BY_TYPE } = PropertyConstants;
    return {
      listingType: LISTING_ENUM_BY_TYPE[formData.listingType],
      propertyType: PROPERTY_ENUM_BY_TYPE[formData.propertyType],
      fullAddress: formData.fullAddress,
      unitNumber: formData.unitNumber,
      numUnits: formData.numUnits,
    };
  },
  // TODO: update this once the API is updated with autogenerated TS types
  // eslint-disable-next-line
  transformDatafromApi(apiData: Record<string, any>) {
    const { LISTING_TYPES_BY_CODE, PROPERTY_TYPES_BY_CODE } = PropertyConstants;
    const dataFromApi = apiData;
    // @ts-ignore
    const listingType = LISTING_TYPES_BY_CODE[dataFromApi.listingType.code];
    const propertyType = PROPERTY_TYPES_BY_CODE[dataFromApi.propertyType.code];

    const apiFloorplans = this.transformFloorplanData(dataFromApi.floorPlans);
    const apiUnits = (dataFromApi.units || []).map(Unit);

    let buildingAttributes = null;
    let furnished = null;
    let hardwoodFloors = null;
    if (dataFromApi.hasOwnProperty('buildingAttributes')) {
      buildingAttributes = dataFromApi.buildingAttributes;

      if (buildingAttributes.hasOwnProperty('isFurnished')) {
        furnished = buildingAttributes.isFurnished;
      }

      if (buildingAttributes.hasOwnProperty('floorCoveringTypes')) {
        hardwoodFloors = buildingAttributes.floorCoveringTypes.hasHardwood;
      }
    }

    const vrModels = dataFromApi.vrModels && dataFromApi.vrModels.map((tour: Record<string, unknown>) => VrModel(tour));
    const externalTours =
      dataFromApi.externalTours && dataFromApi.externalTours.map((tour: Record<string, unknown>) => ExternalTour(tour));

    const newFormData = {
      active: dataFromApi.active,
      additionalEmail: dataFromApi.additionalEmail,
      adproduct: dataFromApi.adproduct,
      aiDescription: dataFromApi.aiDescriptionDto,
      applicationsSummary: ApplicationsSummary(dataFromApi.applicationsSummary),
      hasWallAirConditioning: dataFromApi.hasWallAirConditioning,
      hasWindowAirConditioning: dataFromApi.hasWindowAirConditioning,
      hasCentralAirConditioning: dataFromApi.hasCentralAirConditioning,
      hasForcedAirHeatingSystem: dataFromApi.hasForcedAirHeatingSystem,
      hasBaseboardHeatingSystem: dataFromApi.hasBaseboardHeatingSystem,
      hasHeatPumpHeatingSystem: dataFromApi.hasHeatPumpHeatingSystem,
      hasWallHeatingSystem: dataFromApi.hasWallHeatingSystem,
      amenities: dataFromApi.amenities,
      propertyAmenities: dataFromApi.propertyAmenities,
      amenitiesText: '',
      apartmentAmenities: dataFromApi.apartmentAmenities,
      canBeArchived: dataFromApi.canBeArchived,
      canBeRelisted: dataFromApi.canBeRelisted,
      balconyOrDeck: dataFromApi.balconyDeckPatioPorch,
      buildingAttributes: dataFromApi.buildingAttributes,
      contactCount: dataFromApi.contactCount,
      contactEmail: dataFromApi.email,
      contactEmailDeliverability: ContactEmailDeliverability(dataFromApi.contactEmailDeliverability),
      contactHours: {
        sunday: dataFromApi.contactHoursSunday,
        monday: dataFromApi.contactHoursMonday,
        tuesday: dataFromApi.contactHoursTuesday,
        wednesday: dataFromApi.contactHoursWednesday,
        thursday: dataFromApi.contactHoursThursday,
        friday: dataFromApi.contactHoursFriday,
        saturday: dataFromApi.contactHoursSaturday,
      },
      contactName: dataFromApi.contactName,
      contactPhone: dataFromApi.phone,
      contactPhoneDateVerified: dataFromApi.datePhoneVerified,
      city: dataFromApi.city,
      customerId: dataFromApi.customerId,
      dateLastActivated: dataFromApi.dateLastActivated,
      dateLastActivatedTimestamp: dataFromApi.dateLastActivatedTimestamp,
      deliveryMethod: dataFromApi.deliveryMethod,
      description: dataFromApi.fullDescription,
      dateEmailVerified: dataFromApi.dateEmailVerified,
      expirationDate: dataFromApi.expirationDate,
      expirationDateTimestamp: dataFromApi.expirationDateTimestamp,
      externalTours,
      feedId: dataFromApi.feedId,
      feedListingId: dataFromApi.feedListingId,
      listingAlias: dataFromApi.listingAlias,
      feedName: dataFromApi.feedName,
      floorplans: apiFloorplans,
      isListingExclusive: dataFromApi.agent && dataFromApi.agent.isExclusive,
      agentLicenseType: dataFromApi.agent && dataFromApi.agent.licenseType,
      units: apiUnits,
      forRentBy: dataFromApi.forRentBy,
      isFurnished: furnished !== null ? furnished : dataFromApi.isFurnished,
      geo: dataFromApi.geo,
      hasHardwoodFloorCovering: hardwoodFloors !== null ? hardwoodFloors : dataFromApi.hasHardwoodFloorCovering,
      hasActiveMultifamilyContract: dataFromApi.hasActiveMultifamilyContract,
      hasAnalyticsRentComps: dataFromApi.hasAnalyticsRentComps,
      hasAttachedGarageParking: dataFromApi.hasAttachedGarageParking,
      hasDetachedGarageParking: dataFromApi.hasDetachedGarageParking,
      hasBicycleStorage: dataFromApi.hasBicycleStorage,
      hasDishWasherAppliance: dataFromApi.hasDishWasherAppliance,
      hasSwimmingPool: dataFromApi.hasSwimmingPool,
      hideAddress: dataFromApi.hideStreetAddress,
      hidePhone: dataFromApi.hidePhone,
      //  ZRM-10946: API omits isAddressValid on feed listings (for which address is not editable).
      //  Default to true if value doesn't exist on response
      isAddressValid: typeof dataFromApi.isAddressValid !== 'undefined' ? dataFromApi.isAddressValid : true,
      isArchived: dataFromApi.isArchived,
      isContactEmailVerified: dataFromApi.isContactEmailVerified,
      singleFamilyContract: SingleFamilyContract(dataFromApi.singleFamilyContractDto),
      isAddressEditable: dataFromApi.isAddressEditable,
      isEditable: dataFromApi.isEditable,
      isConvertedFromFeed: dataFromApi.isConvertedFromFeed,
      isFeed: dataFromApi.isFeed,
      isViewable: dataFromApi.isViewable,
      isMultifamily: dataFromApi.isMultifamily,
      isPaidMultifamily: dataFromApi.isPaidMultifamily,
      isApplicationsTogglePristine: dataFromApi.isApplicationsTogglePristine,
      isApplicationsAccepted: dataFromApi.isApplicationsAccepted,
      isApplicationsInviteOnly: dataFromApi.isApplicationsInviteOnly,
      isApplicationsInviteOnlyEnabled: dataFromApi.isApplicationsInviteOnlyEnabled,
      isPackageProduct: dataFromApi.isPackageProduct,
      isWaitlisted: dataFromApi.isWaitlisted,
      laundry: dataFromApi.laundry,
      leaseDuration: dataFromApi.leaseDuration,
      leaseTerms: dataFromApi.leaseTerms,
      listingAvailability: dataFromApi.listingAvailability,
      listingId: dataFromApi.listingId,
      listingTitle: dataFromApi.name,
      listingType,
      isContactInfoEditable: dataFromApi.isContactInfoEditable,
      listingTypeCode: dataFromApi.listingType.code,
      logo: dataFromApi.logo,
      modularListingProgress: dataFromApi.modularListingProgress,
      name: dataFromApi.name,
      numUnits: dataFromApi.numUnits,
      hasZillowRentConnectBasePackage: dataFromApi.hasZillowRentConnectBasePackage,
      isActivationBlocked: dataFromApi.isActivationBlocked,
      housingConnectorStatus: dataFromApi.housingConnectorStatus,
      offStreetParking: dataFromApi.offStreetParking,
      localeSpecificNotice: dataFromApi.localeSpecificNotice,
      partialListing: dataFromApi.partialListing,
      petsAllowed: dataFromApi.petsAllowed,
      photos: dataFromApi.propertyPhotos,
      preferredContactEmail: dataFromApi.preferredContactEmail,
      productType: dataFromApi.productType,
      propertyTypeCode: dataFromApi.propertyType.code,
      propertyType,
      rentersInsurance: dataFromApi.rentersInsurance,
      rentGuarantee: dataFromApi.rentGuarantee,
      rentZestimate: dataFromApi.rentZestimate,
      restrictions: dataFromApi.restrictions,
      roomForRent: dataFromApi.roomForRent,
      schedule: dataFromApi.schedule,
      selfServeOnboardingStatus: dataFromApi.selfServeOnboardingStatus,
      specials: dataFromApi.previewMessage,
      specialOffers: dataFromApi.specialOffers,
      state: dataFromApi.state,
      street: dataFromApi.street,
      syndication: dataFromApi.syndication,
      topDupesDetails: dataFromApi.topDupesDetails,
      trackingPhoneNumber: dataFromApi.trackingPhoneNumber,
      unit: dataFromApi.unit,
      verified: !dataFromApi.needsPhoneVerification,
      views: dataFromApi.views,
      website: dataFromApi.website,
      wheelchairAccess: dataFromApi.wheelchairAccess,
      zip: dataFromApi.zip,
      vrModels,
      multiToSingleFamilyConversion: dataFromApi.multiToSingleFamilyConversion,
      dateCreated: dataFromApi.dateCreated,
      updateActiveStatus: dataFromApi.updateActiveStatus,
      listingCriteria: dataFromApi.listingCriteria,
      videoUrl: dataFromApi.videoUrl,
      webAnalytics: dataFromApi.webAnalytics,
    };
    return newFormData;
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transformFloorplanData(floorplans: Array<Record<string, any>> = []) {
    const apiFloorplans = (floorplans || []).map((floorplan) => ({
      floorPlanId: floorplan.id,
      active: typeof floorplan.active === 'undefined' ? true : floorplan.active,
      layoutPhoto: floorplan.layout || {},
      floorPlanPhotos: floorplan.floorPlanPhotos || [],
      price: this.formatPriceFromApi(floorplan.lowPrice, floorplan.highPrice) || '',
      lowPrice: floorplan.lowPrice || '',
      highPrice: floorplan.highPrice || '',
      roomForRent: floorplan.roomForRent,
      securityDeposit: floorplan.securityDeposit,
      baths: floorplan.numBathrooms || '',
      beds: this.formatBedsFromApi(floorplan.numBedrooms),
      squareFeet: floorplan.squareFeet || '',
      dateAvailable: floorplan.dateAvailable || '',
      name: floorplan.name || '',
      unitAmenities: floorplan.unitAmenities || [],
      units: (floorplan.units || []).map(Unit),
      vrModel: VrModel(floorplan.vrModel),
      externalTour: ExternalTour(floorplan.externalTour),
    }));

    return apiFloorplans;
  },
};

export default postingUtils;
